import { useState } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('Thank you for your message! We will get back to you shortly.');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d96i03cut53jbls7ko90', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const serverMsg = (parsed?.meta as Record<string, string> | undefined)?.message
        || (parsed?.meta as Record<string, string> | undefined)?.detail
        || (parsed as Record<string, string> | null)?.message
        || responseText;

      if (response.ok && (parsed as Record<string, string> | null)?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thank you for your message! We will get back to you shortly.');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again later.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary-500 uppercase tracking-widest mb-3">
              Get in Touch
            </p>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              Contact Us
            </h1>
            <p className="mt-3 text-base text-foreground-600 max-w-xl mx-auto">
              Have questions about our courses or need help choosing the right path? We would love to hear from you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-background-100 border border-background-200 rounded-lg p-6">
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                  AIVO SOLUTION SDN BHD
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">Address</p>
                      <p className="text-sm text-foreground-600">
                        Level 8, Tower A, Menara UOA Bangsar<br />
                        No. 5 Jalan Bangsar Utama 1<br />
                        59000 Kuala Lumpur, Malaysia
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line w-4 h-4 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">Email</p>
                      <a href="mailto:admin@ggcourse.com" className="text-sm text-primary-500 hover:text-primary-600">
                        admin@ggcourse.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                      <i className="ri-time-line w-4 h-4 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">Business Hours</p>
                      <p className="text-sm text-foreground-600">
                        Monday — Friday: 9:00 AM — 6:00 PM (MYT)<br />
                        Saturday: 9:00 AM — 1:00 PM (MYT)<br />
                        Sunday & Public Holidays: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 border border-background-200 rounded-lg p-6">
                <h3 className="text-sm font-heading font-bold text-foreground-950 mb-3">
                  Quick Links
                </h3>
                <div className="space-y-2">
                  <Link to="/pricing" className="block text-sm text-primary-500 hover:text-primary-600">Pricing &amp; Plans</Link>
                  <Link to="/refund-policy" className="block text-sm text-primary-500 hover:text-primary-600">Refund Policy</Link>
                  <Link to="/terms-of-service" className="block text-sm text-primary-500 hover:text-primary-600">Terms of Service</Link>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8 space-y-5"
              >
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">
                  Send Us a Message
                </h3>

                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium text-foreground-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    required
                    className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-200 resize-none"
                    placeholder="Tell us what you need help with..."
                  />
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                />

                {formStatus === 'success' && (
                  <div className="bg-accent-100 border border-accent-200 rounded-md px-4 py-3 text-sm text-accent-800">
                    <i className="ri-check-line w-4 h-4 inline-flex items-center justify-center mr-1" />
                    {formMessage}
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">
                    <i className="ri-error-warning-line w-4 h-4 inline-flex items-center justify-center mr-1" />
                    {formMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed h-11 px-6 text-sm"
                >
                  {formStatus === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}